import { rowPayload, type SyncManifest, type SyncRow, type SyncSnapshot, type SyncStatus } from "./types"

export type ShapeMessage = {
  key?: string
  value?: { id?: string; key?: string; payload?: unknown }
  headers: { operation?: "insert" | "update" | "delete"; control?: string }
}
export function compareRevision(a: string, b: string) {
  const [av, ad = ""] = a.split(":"), [bv, bd = ""] = b.split(":")
  return BigInt(av || "0") === BigInt(bv || "0") ? ad.localeCompare(bd) : BigInt(av || "0") > BigInt(bv || "0") ? 1 : -1
}
export type CollectionState = { manifest: SyncManifest; rows: SyncRow[]; status: SyncStatus; complete: boolean }

/** Materializes complete rows, including partial Electric UPDATEs. Initial
 * chunks are staged until up-to-date; resyncs replace the entire collection so
 * deleted rows cannot linger. A late replica snapshot never replaces newer SSR. */
export class ProjectionCollection {
  state: CollectionState
  private stage = new Map<string, SyncRow>()
  private changed = false
  constructor(manifest: SyncManifest, initial?: SyncSnapshot) {
    this.state = { manifest: initial?.manifest ?? manifest, rows: initial?.rows ?? [], status: "connecting", complete: !!initial }
  }
  reset() { this.stage.clear(); this.changed = true }
  resume() { this.stage = new Map(this.state.rows.map(row => [row.key, row])); this.changed = false }
  seed(snapshot: SyncSnapshot) {
    if (snapshot.manifest.scope !== this.state.manifest.scope || snapshot.manifest.viewer !== this.state.manifest.viewer) return
    if (!this.state.complete || compareRevision(snapshot.manifest.revision, this.state.manifest.revision) > 0) {
      this.state = { ...this.state, manifest: snapshot.manifest, rows: snapshot.rows, complete: true }
    }
  }
  consume(messages: ShapeMessage[]): "reset" | "commit" | "chunk" {
    if (messages.some(message => message.headers.control === "must-refetch")) { this.reset(); return "reset" }
    for (const message of messages) {
      const key = message.value?.key ?? message.value?.id?.split(":").slice(1).join(":")
        ?? message.key?.split("/").pop()?.replace(/^"|"$/g, "").split(":").slice(1).join(":")
      if (!key || !message.headers.operation) continue
      this.changed = true
      if (message.headers.operation === "delete") this.stage.delete(key)
      else if (message.value && "payload" in message.value) this.stage.set(key, { key, payload: rowPayload(message.value.payload) })
    }
    if (!messages.some(message => message.headers.control === "up-to-date")) return "chunk"
    const meta = this.stage.get("$meta")?.payload as { _sync?: { revision: string; builtAt: string } } | undefined
    if (!meta?._sync) return "chunk"
    if (!this.state.complete || compareRevision(meta._sync.revision, this.state.manifest.revision) >= 0) {
      if (this.changed || !this.state.complete) this.state = { ...this.state, rows: [...this.stage.values()], complete: true,
        manifest: { ...this.state.manifest, ...meta._sync } }
      this.changed = false
      return "commit"
    }
    return "chunk"
  }
  setStatus(status: SyncStatus) { this.state = { ...this.state, status } }
  deny() { this.state = { ...this.state, rows: [], complete: false, status: "denied" }; this.reset() }
}
